import React from 'react';
import { motion } from 'motion/react';
import type { WeddingMediaItem } from '@/src/types';
import { Reveal } from './shared';

type Props = {
  title: string;
  description: string;
  moments: WeddingMediaItem[];
};

export function PreWeddingStorySection({ title, description, moments }: Props) {
  const visibleMoments = moments.filter((moment) => moment.url?.trim());
  if (visibleMoments.length === 0 && !title.trim() && !description.trim()) return null;

  return (
    <section className="bg-[#ead8cf] px-5 py-20 text-[#211712] sm:px-8 lg:px-14 lg:py-28">
      <div className="grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
        <Reveal className="lg:sticky lg:top-16 lg:h-fit">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#8f514a]">Pre-Wedding Story</p>
          {title.trim() && (
            <h2 className="mt-4 text-[clamp(2.35rem,5.8vw,6rem)] font-semibold leading-[0.98] tracking-normal">
              {title}
            </h2>
          )}
          {description.trim() && (
            <p className="mt-7 max-w-xl text-lg leading-8 text-[#5a4038] sm:text-xl sm:leading-9">{description}</p>
          )}
        </Reveal>

        <div className="space-y-10">
          {visibleMoments.map((moment, index) => (
            <Reveal key={`${moment.url}-${index}`} delay={index * 0.06}>
              <motion.figure
                whileHover={{ scale: 0.992 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className={`grid items-end gap-5 ${index % 2 === 0 ? 'md:grid-cols-[1.2fr_0.8fr]' : 'md:grid-cols-[0.8fr_1.2fr]'}`}
              >
                <div className={index % 2 === 0 ? '' : 'md:order-2'}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#211712] md:aspect-[16/11]">
                    {moment.type === 'video' ? (
                      <video src={moment.url} className="h-full w-full object-cover" controls playsInline />
                    ) : (
                      <img src={moment.url} alt="" className="h-full w-full object-cover" />
                    )}
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#211712]/42 via-transparent to-transparent" />
                  </div>
                </div>
                <figcaption className="border-t border-[#211712]/20 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8f514a]">
                    Memory {String(index + 1).padStart(2, '0')}
                  </p>
                  {moment.caption && <h3 className="mt-3 text-3xl font-semibold leading-tight">{moment.caption}</h3>}
                  {moment.description && <p className="mt-4 text-base leading-7 text-[#5a4038]">{moment.description}</p>}
                </figcaption>
              </motion.figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
